import React from "react";
import { ThemeUIStyleObject } from "theme-ui";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { ButtonLink } from "@shared-components/Button/Button";
import ParseHtmlToReact from "@shared-components/ParseHtmlToReact/ParseHtmlToReact";
import { TextModule } from "src/carlsberg-headless/types/modules/Text";
import { Container } from "@shared-components/Container/Container";
import { THEMES_MAP } from "../../carlsberg-headless/themes-map";
import { TAXONOMIES_MAP } from "../../carlsberg-headless/taxonomies-map";
import { getThemeVariantStyles } from "@styles/utils/getThemeVariant";
import { textContainerStyle, textContainerFaqStyle } from "./styles";

import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map";
export interface TextModuleProps extends TextModule {
  sx?: ThemeUIStyleObject;
  questionText?: string;
  answerText?: string;
  whiteHeading?: boolean;
}

const PureText: React.FC<TextModuleProps> = (props) => {
  let textAlignment = getThemeVariantStyles("textModule", "alignment.default");
  switch (true) {
    case props.themes?.includes(THEMES_MAP.text_align.text_centering):
      textAlignment = getThemeVariantStyles("textModule", "alignment.center");
      break;
    case props.themes?.includes(THEMES_MAP.text_align.text_left):
      textAlignment = getThemeVariantStyles("textModule", "alignment.left");
      break;
  }

  let sectionTextColor = { color: "darkGreen" } as ThemeUIStyleObject;
  let headingTextColor = { color: "darkGreen" } as ThemeUIStyleObject;
  let richTextStyles = {
    color: "darkGray",
    h3: { color: "darkGreen" },
    ".secondary": {
      color: "darkGreen",
    },
  } as ThemeUIStyleObject;
  let buttonVariant = "green";
  switch (true) {
    case props.themes?.includes(THEMES_MAP.layout_bg.layout_bg_red):
      sectionTextColor = { color: "white" };
      headingTextColor = { color: "copper" };
      richTextStyles = {
        color: "white",
        ".secondary": {
          color: "copper",
          a: {
            color: "white",
          },
        },
        a: {
          color: "copper",
          "&:hover, &:focus": {
            textDecoration: "underline",
          },
        },
      };
      buttonVariant = "copper";
      break;
    case props.themes?.includes(THEMES_MAP.layout_bg.layout_bg_green):
      sectionTextColor = { color: "white" };
      headingTextColor = { color: props.whiteHeading ? "white" : "copper" };
      richTextStyles = {
        color: "white",
        ".secondary": {
          color: "copper",
          a: {
            color: "white",
          },
        },
        a: {
          color: "copper",
          "&:hover, &:focus": {
            textDecoration: "underline",
          },
        },
      };
      buttonVariant = "copper";
      break;
    case props.themes?.includes(THEMES_MAP.layout_bg.layout_bg_lightergray):
    case props.themes?.includes(THEMES_MAP.text_color.text_color_green):
      sectionTextColor = { color: "darkGreen" };
      headingTextColor = { color: "darkGreen" };
      richTextStyles = {
        color: "darkGray",
        ".secondary": {
          color: "darkGreen",
        },
      };
      buttonVariant = "green";
      break;
    case props.themes?.includes(THEMES_MAP.text_color.text_color_red):
      sectionTextColor = { color: "darkRed" };
      headingTextColor = { color: "darkRed" };
      richTextStyles = {
        color: "darkGray",
        ".secondary": {
          color: "darkRed",
        },
      };
      buttonVariant = "red";
      break;
  }

  // If text module is shown as Hero for FAQ
  const showAsFaqHero = props.taxonomies?.includes(
    TAXONOMIES_MAP.text_module.text_module_as_faq_hero
  );
  if (showAsFaqHero) {
    sectionTextColor = { color: "copper", fontWeight: "normal" };
    headingTextColor = { color: "white" };
    richTextStyles = {
      color: "white",
      p: {
        color: "white",
        fontSize: ["paragraphMedium", "paragraphMedium", "paragraph"],
      },
      a: {
        color: "white",
        textDecoration: "underline",
        "&:hover, &:focus": {
          textDecoration: "none",
        },
      },
    };
  }
  return (
    <Container
      sx={{
        ...textContainerStyle,
        ...(showAsFaqHero && { ...textContainerFaqStyle }),
        ...props.sx,
      }}
    >
      <Box
        key={props.cbKey}
        sx={
          !showAsFaqHero
            ? { ...textAlignment }
            : { textAlign: ["center", "center", "left"] }
        }
      >
        {((showAsFaqHero && props.questionText) ||
          (!showAsFaqHero && props.sectionTitle)) && (
          <Heading
            as="p"
            variant="sectionText"
            sx={{
              marginBottom: !showAsFaqHero ? [9, 9, 14] : [4],
              ...sectionTextColor,
            }}
          >
            {props.sectionTitle || props.questionText}
          </Heading>
        )}

        {props.headline && (
          <Heading
            as={showAsFaqHero ? "h1" : "h2"}
            sx={{
              marginBottom: !showAsFaqHero
                ? [7, 7, 11]
                : props.content
                ? [13, 14, 15]
                : [0],
              fontSize: ["h3", "h2", "h1"],
              ...headingTextColor,
            }}
          >
            {props.headline}
          </Heading>
        )}

        {showAsFaqHero && props.answerText && (
          <Heading
            as="h2"
            variant="sectionText"
            sx={{
              marginBottom: !showAsFaqHero ? [9, 9, 14] : [4],
              ...sectionTextColor,
            }}
          >
            {props.answerText}
          </Heading>
        )}

        {props.content && (
          <ParseHtmlToReact
            sx={{
              ...richTextStyles,
              ...{
                fontSize: [
                  "paragraphMedium",
                  "paragraphMedium",
                  "paragraphLarge",
                ],
              },
            }}
            html={props.content}
          />
        )}
        {!showAsFaqHero && props.link && (
          <Box sx={{ ...textAlignment }}>
            <ButtonLink
              to={props.link.url}
              {...(props.link.target && { target: props.link.target })}
              variant={buttonVariant}
              outline
              sx={{ marginTop: [8] }}
            >
              {props.link.name}
            </ButtonLink>
          </Box>
        )}
      </Box>
    </Container>
  );
};

const Text: React.FC<TextModuleProps> = (props) => {
  const { get } = useDictionaryContext();
  const { currentCulture } = useSiteContext();
  return (
    <PureText
      questionText={get(
        DICTIONARY_MAP.faq_module.faq_module_question_text,
        currentCulture
      )}
      answerText={get(
        DICTIONARY_MAP.faq_module.faq_module_answer_text,
        currentCulture
      )}
      {...props}
    />
  );
};
export { Text, PureText };
